import { useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import { queryKeys } from "./queryKeys";
import {
  ScreenAction,
  ScreenAssistantConversation,
  ScreenAssistantMessage,
  ScreenContext,
  ScreenTitle,
} from "../types";

const BASE_URL = "/api/screen-assistant";

/** How often a pending reply is re-read until the model finishes. */
export const SCREEN_ASSISTANT_POLL_MS = 2000;

/** The chip's title is for display only, so a minute-old one is fine. */
const TITLE_STALE_MS = 60_000;

const NO_MESSAGES: ScreenAssistantMessage[] = [];

const pendingReplyOf = (messages: ScreenAssistantMessage[]) =>
  messages.find((m) => m.role === "ASSISTANT" && m.status === "PENDING");

const replaceMessage = (
  conversation: ScreenAssistantConversation | undefined,
  message: ScreenAssistantMessage
): ScreenAssistantConversation | undefined =>
  conversation && {
    ...conversation,
    messages: conversation.messages.map((m) =>
      m.id === message.id ? message : m
    ),
  };

/** Writes an answered proposal back into the reply that carries it. */
const replaceAction = (
  conversation: ScreenAssistantConversation | undefined,
  action: ScreenAction
): ScreenAssistantConversation | undefined =>
  conversation && {
    ...conversation,
    messages: conversation.messages.map((m) =>
      m.actions?.some((a) => a.id === action.id)
        ? {
            ...m,
            actions: m.actions.map((a) => (a.id === action.id ? action : a)),
          }
        : m
    ),
  };

/** The POST returns only the pending reply, so the question is added next to it locally. */
const localQuestion = (
  text: string,
  reply: ScreenAssistantMessage
): ScreenAssistantMessage => ({
  id: `local-${reply.id}`,
  role: "USER",
  text,
  status: "DONE",
  createdAt: reply.createdAt,
});

/** i18n key for a question that could not be sent: 409 means the previous one is still pending. */
export const screenAssistantSendErrorKey = (error: unknown): string | null => {
  if (!error) return null;
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 409
    ? "screenAssistant.errors.pending"
    : "screenAssistant.errors.send";
};

export interface SendOptions {
  /** The screen to ask about; omitted when the user switched the context off. */
  screenContext?: ScreenContext | null;
  /** Runs once the question was accepted, e.g. to clear the input. */
  onSent?: () => void;
}

/**
 * The user's conversation with the floating assistant, within the company they act for.
 *
 * Nothing loads until `enabled` — the panel's first opening. A question is answered
 * asynchronously: the POST returns the reply `PENDING`, and it is polled every
 * {@link SCREEN_ASSISTANT_POLL_MS} until it is `DONE` or `FAILED`.
 */
export const useScreenAssistant = ({
  enabled,
  companyId,
}: {
  enabled: boolean;
  companyId: string | null | undefined;
}) => {
  const queryClient = useQueryClient();
  const conversationKey = useMemo(
    () => [queryKeys.SCREEN_ASSISTANT_CONVERSATION, companyId ?? null],
    [companyId]
  );

  const conversation = useQuery({
    queryKey: conversationKey,
    queryFn: async (): Promise<ScreenAssistantConversation> => {
      const { data } = await axios.get(`${BASE_URL}/conversation`);
      return data;
    },
    enabled,
  });

  const messages = conversation.data?.messages ?? NO_MESSAGES;
  const pendingId = pendingReplyOf(messages)?.id;

  const poll = useQuery({
    queryKey: [queryKeys.SCREEN_ASSISTANT_MESSAGE, pendingId],
    queryFn: async (): Promise<ScreenAssistantMessage> => {
      const { data } = await axios.get(`${BASE_URL}/messages/${pendingId}`);
      return data;
    },
    enabled: Boolean(pendingId),
    refetchInterval: (query) =>
      query.state.data && query.state.data.status !== "PENDING"
        ? false
        : SCREEN_ASSISTANT_POLL_MS,
  });

  const polled = poll.data;
  useEffect(() => {
    if (!polled || polled.status === "PENDING" || polled.id !== pendingId) {
      return;
    }
    queryClient.setQueryData<ScreenAssistantConversation>(
      conversationKey,
      (old) => replaceMessage(old, polled)
    );
  }, [polled, pendingId, queryClient, conversationKey]);

  const send = useMutation({
    mutationFn: async ({
      text,
      screenContext,
    }: {
      text: string;
      screenContext?: ScreenContext | null;
    }): Promise<ScreenAssistantMessage> => {
      const { data } = await axios.post(`${BASE_URL}/messages`, {
        text,
        ...(screenContext ? { screenContext } : {}),
      });
      return data;
    },
    onSuccess: (reply, { text }) => {
      queryClient.setQueryData<ScreenAssistantConversation>(
        conversationKey,
        (old) => ({
          id: old?.id ?? null,
          messages: [
            ...(old?.messages ?? []),
            localQuestion(text, reply),
            reply,
          ],
        })
      );
    },
    onError: (error) => {
      // Another tab has a question pending: reload so its answer shows up here too.
      if (
        screenAssistantSendErrorKey(error) === "screenAssistant.errors.pending"
      ) {
        queryClient.invalidateQueries({ queryKey: conversationKey });
      }
    },
  });

  const answerAction = useMutation({
    mutationFn: async ({
      actionId,
      answer,
    }: {
      actionId: string;
      answer: "confirm" | "cancel";
    }): Promise<ScreenAction> => {
      const { data } = await axios.post(
        `${BASE_URL}/actions/${actionId}/${answer}`
      );
      return data;
    },
    onSuccess: (action) =>
      queryClient.setQueryData<ScreenAssistantConversation>(
        conversationKey,
        (old) => replaceAction(old, action)
      ),
    // Answered elsewhere (another tab, a double click): reload to show where it stands.
    onError: () => queryClient.invalidateQueries({ queryKey: conversationKey }),
  });

  const startOver = useMutation({
    mutationFn: async (): Promise<ScreenAssistantConversation> => {
      const { data } = await axios.post(`${BASE_URL}/conversations`);
      return data;
    },
    onSuccess: (fresh) => queryClient.setQueryData(conversationKey, fresh),
  });

  return {
    messages,
    isLoading: conversation.isLoading && enabled,
    isLoadError: conversation.isError,
    retry: () => conversation.refetch(),
    /** A question is on its way or its answer is pending: the input waits. */
    isBusy: send.isPending || Boolean(pendingId),
    send: (text: string, { screenContext, onSent }: SendOptions = {}) =>
      send.mutate({ text, screenContext }, { onSuccess: () => onSent?.() }),
    sendErrorKey: screenAssistantSendErrorKey(send.error),
    clearSendError: send.reset,
    startOver: () => startOver.mutate(),
    confirmAction: (actionId: string) =>
      answerAction.mutate({ actionId, answer: "confirm" }),
    cancelAction: (actionId: string) =>
      answerAction.mutate({ actionId, answer: "cancel" }),
    /** The proposal being answered right now, so its buttons can wait. */
    answeringActionId: answerAction.isPending
      ? (answerAction.variables?.actionId ?? null)
      : null,
    actionFailed: answerAction.isError,
    canStartOver: messages.length > 0 && !pendingId && !startOver.isPending,
  };
};

/**
 * The title of the record on screen, for the chip. Read only while the panel is open, and only
 * for display — the backend reads the record again, with its own access check, for the model.
 * A screen the user may not see comes back 404 and the chip falls back to the kind alone.
 */
export const useScreenTitle = (
  context: ScreenContext | null,
  enabled: boolean
) =>
  useQuery({
    queryKey: [queryKeys.SCREEN_ASSISTANT_TITLE, context?.kind, context?.id],
    queryFn: async (): Promise<ScreenTitle> => {
      const { data } = await axios.get(`${BASE_URL}/screens/${context!.kind}`, {
        params: context!.id ? { id: context!.id } : undefined,
      });
      return data;
    },
    enabled: enabled && Boolean(context),
    staleTime: TITLE_STALE_MS,
    retry: false,
  });

/**
 * Downloads a file the assistant prepared (one of its own `/api/...` links), with the user's
 * session, and hands it to the browser to save.
 */
export const downloadScreenAssistantFile = async (path: string) => {
  const response = await axios.get(path, { responseType: "blob" });
  const disposition: string = response.headers?.["content-disposition"] ?? "";
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "reporte.xlsx";
  const url = window.URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};
